import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import {
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  Platform,
} from "react-native";
import { useIsFocused } from "@react-navigation/native";
import { useDispatch, useSelector } from "react-redux";
import Spinner from "../../components/shared/Spinner";
import DeviceOrientationContext from "../../components/context/DeviceOrientationContext";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { MaterialCommunityIcons, Feather } from "@expo/vector-icons";
import { Modal, Pressable } from "react-native";
import {
  mdItemTrackerHeaders,
  itemTrackerHeaders,
  smItemTrackerHeaders,
} from "./utils/headers";
import { DataTableWithPagination } from "../../components/custom/DataTable/DataTableWithPagination";
import TrackerEnquiryModal from "./partials/TrackerEnquiryModal";
import { getMergedTrackerOrders } from "../../redux/dispatcher/productTrackerDispatcher";
import { useToast } from "react-native-toast-notifications";
import { LabelBannerWithSearchBar } from "../../components/custom/Banners/LabelBannerWithSearchBar";
import {
  filterSpecificField,
  resetSearch,
  searchTrackerOrders,
  sortTrackerOrdersByOrderNumber,
} from "../../redux/reducers/productTrackerReducer";
import { ItemTrackerBadgeHorizontalList } from "../../components/custom/Badge/ItemTrackerBadgeHorizontalList";
import { translateHeaders, useTranslation } from "../../utils/useTranslation";
import { customerOrderTrackerTranslationMap } from "../../utils/translations";
import TrackerHistoryModal from "./partials/TrackerHistoryModal";
import TrackerDateModal from "./partials/TrackerDateModal";

export default function ItemTrackerScreen({ navigation }: any) {
  const [modalOpen, setModalOpen] = useState<boolean>();
  const onClose = () => {
    setModalOpen(false);
  };
  const [selectedItem, setSelectedItem] = useState<any>(null);
  const [selectedEnquiryType, setSelectedEnquiryType] = useState<any>(null);
  const isFocused = useIsFocused();
  const [isRefresh, setRefresh] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [mobileFilterOpen, setMobileFilterOpen] = useState<boolean>(false);
  const toast = useToast();
  const [tableHeaders, setTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      itemTrackerHeaders,
    );
  const { t } = useTranslation();
  const { isLargeDevice, isMediumDevice, isMobileDevice, isSmallDevice } =
    useContext(DeviceOrientationContext);
  const columnsToNotHaveThousandsSeparator: Array<string> = ["Packing #"];
  const operationsFilterCategoryList = [
    t("all"),
    t("onHold"),
    t("newOrder"),
    t("arrived"),
    t("shipped"),
    t("rushOrder"),
    t("orderPlaced"),
    t("atRisk"),
    t("closed"),
    t("cancelled"),
  ];

  const dispatch = useDispatch<any>();

  // Column visibility settings
  const [columnSettingsOpen, setColumnSettingsOpen] = useState<boolean>(false);
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([]);
  const [columnOrder, setColumnOrder] = useState<string[]>([]);
  const [settingsBtnPos, setSettingsBtnPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const settingsBtnRef = useRef<any>(null);

  // Load saved hidden columns from storage on mount
  useEffect(() => {
    AsyncStorage.getItem("itemtracker_hidden_columns").then((stored) => {
      if (stored) {
        try {
          setHiddenColumns(JSON.parse(stored));
        } catch { }
      }
    });
  }, []);

  // All available column definitions (translated)
  const getAllItemTrackerHeaders = () => {
    const baseHeaders = isLargeDevice
      ? itemTrackerHeaders
      : isMediumDevice
        ? mdItemTrackerHeaders
        : smItemTrackerHeaders;
    return translateHeaders(baseHeaders, t, customerOrderTrackerTranslationMap);
  };

  // Build filtered + normalized headers from hidden columns
  const getOrderedKeys = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const allKeys = Object.keys(baseHeaders);
    if (columnOrder.length === 0) return allKeys;
    const ordered: string[] = [];
    columnOrder.forEach((k) => { if (allKeys.includes(k)) ordered.push(k); });
    allKeys.forEach((k) => { if (!ordered.includes(k)) ordered.push(k); });
    return ordered;
  };

  const getFilteredHeaders = (baseHeaders: Record<string, Record<string, string | number | boolean>>) => {
    const orderedKeys = getOrderedKeys(baseHeaders);
    const visibleEntries = orderedKeys
      .filter((key) => !hiddenColumns.includes(key))
      .map((key) => [key, baseHeaders[key]] as [string, Record<string, string | number | boolean>]);
    const totalOriginalFlex = Object.values(baseHeaders).reduce((sum, h: any) => sum + (Number(h.width) || 0), 0);
    const totalVisibleFlex = visibleEntries.reduce((sum, [, h]: any) => sum + (Number(h.width) || 0), 0);
    const scale = totalVisibleFlex > 0 ? (totalOriginalFlex / totalVisibleFlex) : 1;

    const result: Record<string, Record<string, string | number | boolean>> = {};
    visibleEntries.forEach(([key, value]: any) => {
      result[key] = { ...value, width: Number(value.width) * scale };
    });
    return result;
  };

  const toggleColumn = (columnKey: string) => {
    setHiddenColumns((prev) => {
      const updated = prev.includes(columnKey)
        ? prev.filter((k) => k !== columnKey)
        : [...prev, columnKey];
      AsyncStorage.setItem("itemtracker_hidden_columns", JSON.stringify(updated));
      return updated;
    });
  };

  const moveColumn = useCallback((fromKey: string, toKey: string) => {
    const allHeaders = getAllItemTrackerHeaders();
    const currentKeys = getOrderedKeys(allHeaders).filter((k) => k !== "");
    const fromIdx = currentKeys.indexOf(fromKey);
    const toIdx = currentKeys.indexOf(toKey);
    if (fromIdx < 0 || toIdx < 0 || fromIdx === toIdx) return;
    const newKeys = [...currentKeys];
    newKeys.splice(fromIdx, 1);
    newKeys.splice(toIdx, 0, fromKey);
    newKeys.push("");
    setColumnOrder(newKeys);
    AsyncStorage.setItem("item_tracker_column_order", JSON.stringify(newKeys));
  }, [columnOrder]);

  const resetColumnOrder = () => {
    setColumnOrder([]);
    AsyncStorage.removeItem("item_tracker_column_order");
  };

  // Drag-and-drop reordering via window mouse events
  const [dragActiveKey, setDragActiveKey] = useState<string | null>(null);
  const [dragOverKey, setDragOverKey] = useState<string | null>(null);
  const dragRef = useRef<{ activeKey: string | null; overKey: string | null }>({ activeKey: null, overKey: null });

  const startColumnDrag = useCallback((key: string) => {
    if (Platform.OS !== "web" || typeof window === "undefined") return;
    dragRef.current.activeKey = key;
    setDragActiveKey(key);

    const onMouseMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY);
      const row = el && (el as HTMLElement).closest ? (el as HTMLElement).closest("[data-columnkey]") : null;
      const overKey = row ? row.getAttribute("data-columnkey") : null;
      if (overKey !== dragRef.current.overKey) {
        dragRef.current.overKey = overKey;
        setDragOverKey(overKey && overKey !== dragRef.current.activeKey ? overKey : null);
      }
    };

    const onMouseUp = () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      (document.body.style as any).userSelect = "";
      const from = dragRef.current.activeKey;
      const to = dragRef.current.overKey;
      if (from && to && from !== to) {
        moveColumn(from, to);
      }
      dragRef.current = { activeKey: null, overKey: null };
      setDragActiveKey(null);
      setDragOverKey(null);
    };

    document.body.style.cursor = "grabbing";
    (document.body.style as any).userSelect = "none";
    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup", onMouseUp);
  }, [moveColumn]);
  const {
    loading,
    filteredOrders,
    sortDirection,
    orders,
    isProductTrackerLoaded,
  } = useSelector((state: any) => state.productTracker);

  const listRef = useRef<ScrollView>(null);
  const language = useSelector((state: any) => state.configurations.language);
  const [contentOffSet, setContentOffSet] = useState<number>(0);
  const handleScroll = (event: any) => {
    setContentOffSet(event.nativeEvent.contentOffset.y);
  };
  const [nestedTableHeaders, setNestedTableHeaders] =
    useState<Record<string, Record<string, string | number | boolean>>>(
      itemTrackerHeaders,
    );

  const fetchTrackerOrders = () => {
    dispatch(getMergedTrackerOrders());
  };

  const navigateToOrder = (order: any, action: string) => {
    if (action === "view") {
      navigation.navigate("ProductTrackerDetailsReport", { order });
    } else {
      setModalOpen(true);
      setSelectedItem(order);
      setSelectedEnquiryType(action);
    }
  };

  const navigatePressAction = (filterField: string, filterValue: string) => {
    // dispatch here
    if (
      filterValue !== undefined &&
      filterValue !== null &&
      !(typeof filterValue === "string" && filterValue.trim() === "")
    ) {
      if (["OrderNumber1", "ItemName", "BPName"].includes(filterField)) {
        dispatch(
          filterSpecificField({
            filterField: filterField,
            filterValue: filterValue,
          }),
        );
      } else if (
        ["OrderDate", "ReadyByDate", "ETA", "RequestDate"].includes(filterField)
      ) {
        // show calender
        setModalOpen(true);
        setSelectedItem(filterValue.trim());
        setSelectedEnquiryType("date");
      }
    }
  };

  useEffect(() => {
    const filtered = getFilteredHeaders(getAllItemTrackerHeaders());
    setNestedTableHeaders(filtered);
    setTableHeaders(filtered);

    if (isRefresh) {
      fetchTrackerOrders();
      setRefresh(false);
    }

    if ((!orders || orders.length === 0) && !isProductTrackerLoaded) {
      fetchTrackerOrders();
    }
  }, [isFocused, isRefresh, orders, language, isLargeDevice, isMediumDevice, hiddenColumns, columnOrder]);

  const handleSearch = (searchedText: string) => {
    if (searchedText) {
      dispatch(searchTrackerOrders(searchedText));
    } else {
      dispatch(resetSearch());
    }
  };

  const renderFilters = () => (
    <View
      style={[styles.renderFilterStyles, !isLargeDevice && styles.mobileLayout]}
    >
      {isSmallDevice || isMobileDevice ? (
        <>
          <LabelBannerWithSearchBar
            titleName={`${t("totalItems")}: ${filteredOrders.length}`}
            handleSearch={handleSearch}
            setRefresh={setRefresh}
            dataToExport={filteredOrders}
            fileName="ItemTrackerScreen"
            sheetName="Item Tracker Report Screen"
            extraControls={
              <View style={{ flexDirection: "row", gap: 8 }}>
                <View>
                  <TouchableOpacity
                    ref={settingsBtnRef}
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      backgroundColor: columnSettingsOpen ? "#E0E7FF" : "#F3F4F6",
                      justifyContent: "center",
                      alignItems: "center",
                      borderWidth: 1,
                      borderColor: columnSettingsOpen ? "#818CF8" : "#E2E8F0",
                    }}
                    onPress={() => {
                      if (settingsBtnRef.current) {
                        settingsBtnRef.current.measureInWindow(
                          (x: number, y: number, width: number, height: number) => {
                            setSettingsBtnPos({ x, y, width, height });
                            setColumnSettingsOpen(!columnSettingsOpen);
                          }
                        );
                      } else {
                        setColumnSettingsOpen(!columnSettingsOpen);
                      }
                    }}
                  >
                    <MaterialCommunityIcons
                      name="cog-outline"
                      size={18}
                      color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                    />
                  </TouchableOpacity>
                  <Modal
                    visible={columnSettingsOpen}
                    transparent={true}
                    animationType="fade"
                    onRequestClose={() => setColumnSettingsOpen(false)}
                  >
                    <Pressable
                      style={{
                        flex: 1,
                        backgroundColor: "rgba(0,0,0,0.15)",
                      }}
                      onPress={() => setColumnSettingsOpen(false)}
                    >
                      <Pressable
                        style={{
                          position: "absolute",
                          top: settingsBtnPos.y + settingsBtnPos.height + 6,
                          left: settingsBtnPos.x - 220 + 34, // Align to right of button on mobile
                          backgroundColor: "#fff",
                          borderRadius: 12,
                          borderWidth: 1,
                          borderColor: "#E2E8F0",
                          paddingVertical: 10,
                          minWidth: 220,
                          shadowColor: "#000",
                          shadowOffset: { width: 0, height: 8 },
                          shadowOpacity: 0.15,
                          shadowRadius: 20,
                          elevation: 10,
                        }}
                        onPress={(e) => e.stopPropagation()}
                      >
                        <Text
                          style={{
                            fontSize: 12,
                            fontWeight: "700",
                            color: "#6B7280",
                            paddingHorizontal: 16,
                            paddingBottom: 8,
                            textTransform: "uppercase",
                            letterSpacing: 0.5,
                          }}
                        >
                          Column Visibility
                        </Text>
                        <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 4 }} />
                        {/* Select All */}
                        <TouchableOpacity
                          style={{
                            flexDirection: "row",
                            alignItems: "center",
                            paddingVertical: 8,
                            paddingHorizontal: 16,
                          }}
                          onPress={() => {
                            const allKeys = Object.keys(getAllItemTrackerHeaders()).filter((k) => k !== "");
                            if (hiddenColumns.length === 0) {
                              const updated = [...allKeys];
                              setHiddenColumns(updated);
                              AsyncStorage.setItem("itemtracker_hidden_columns", JSON.stringify(updated));
                            } else {
                              setHiddenColumns([]);
                              AsyncStorage.setItem("itemtracker_hidden_columns", JSON.stringify([]));
                            }
                          }}
                        >
                          <View
                            style={{
                              width: 20,
                              height: 20,
                              borderRadius: 4,
                              borderWidth: 1.5,
                              borderColor: hiddenColumns.length === 0 ? "#56B7FD" : "#D1D5DB",
                              backgroundColor: hiddenColumns.length === 0 ? "#56B7FD" : "#fff",
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: 10,
                            }}
                          >
                            {hiddenColumns.length === 0 && (
                              <Feather name="check" size={13} color="#fff" />
                            )}
                          </View>
                          <Text
                            style={{
                              fontSize: 14,
                              color: "#111827",
                              fontWeight: "700",
                            }}
                          >
                            Select All
                          </Text>
                        </TouchableOpacity>
                        <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 2 }} />
                        <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                          {getOrderedKeys(getAllItemTrackerHeaders()).map((key) => {
                            if (key === "") return null;
                            const value = getAllItemTrackerHeaders()[key];
                            if (!value) return null;
                            if ((value as any).display === "none" || (value as any).display === "hidden") return null;
                            const isVisible = !hiddenColumns.includes(key);
                            const isDragging = dragActiveKey === key;
                            const isDragOver = dragOverKey === key;
                            return (
                              <View
                                key={key}
                                {...{ dataSet: { columnkey: key } } as any}
                                style={{
                                  flexDirection: "row",
                                  alignItems: "center",
                                  paddingVertical: 8,
                                  paddingHorizontal: 16,
                                  opacity: isDragging ? 0.35 : 1,
                                  backgroundColor: isDragOver ? "#EFF6FF" : "transparent",
                                  borderTopWidth: isDragOver ? 2 : 0,
                                  borderTopColor: "#56B7FD",
                                }}
                              >
                                <Pressable
                                  onPressIn={() => startColumnDrag(key)}
                                  style={{ padding: 4, marginRight: 6, cursor: "grab" } as any}
                                >
                                  <Feather name="menu" size={14} color="#9CA3AF" />
                                </Pressable>
                                <TouchableOpacity
                                  style={{ flexDirection: "row", alignItems: "center", flex: 1 }}
                                  onPress={() => toggleColumn(key)}
                                >
                                  <View
                                    style={{
                                      width: 20,
                                      height: 20,
                                      borderRadius: 4,
                                      borderWidth: 1.5,
                                      borderColor: isVisible ? "#56B7FD" : "#D1D5DB",
                                      backgroundColor: isVisible ? "#56B7FD" : "#fff",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      marginRight: 10,
                                    }}
                                  >
                                    {isVisible && (
                                      <Feather name="check" size={13} color="#fff" />
                                    )}
                                  </View>
                                  <Text
                                    style={{
                                      fontSize: 14,
                                      color: isVisible ? "#111827" : "#9CA3AF",
                                      fontWeight: isVisible ? "600" : "400",
                                    }}
                                  >
                                    {(value as any).name}
                                  </Text>
                                </TouchableOpacity>
                              </View>
                            );
                          })}
                        </ScrollView>
                        {columnOrder.length > 0 && (
                          <>
                            <View style={{ height: 1, backgroundColor: "#F1F5F9", marginTop: 4 }} />
                            <TouchableOpacity
                              style={{
                                flexDirection: "row",
                                alignItems: "center",
                                justifyContent: "center",
                                paddingVertical: 10,
                                paddingHorizontal: 16,
                              }}
                              onPress={resetColumnOrder}
                            >
                              <Feather name="rotate-ccw" size={13} color="#6B7280" />
                              <Text style={{ fontSize: 13, color: "#6B7280", fontWeight: "600", marginLeft: 6 }}>
                                Reset Order
                              </Text>
                            </TouchableOpacity>
                          </>
                        )}
                      </Pressable>
                    </Pressable>
                  </Modal>
                </View>
              </View>
            }
          />
          <View style={styles.mobileFilterWrapper}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              <TouchableOpacity
                style={[
                  styles.mobileDropdownButton,
                  mobileFilterOpen && styles.mobileDropdownButtonActive,
                ]}
                onPress={() => setMobileFilterOpen(!mobileFilterOpen)}
              >
                <Text style={styles.mobileDropdownLabel}>
                  {t("quickFilter")}:{" "}
                </Text>
                <Text style={styles.mobileDropdownValue}>
                  {selectedCategories.length === 0
                    ? t("all")
                    : selectedCategories.length === 1
                      ? selectedCategories[0]
                      : `${selectedCategories.length} selected`}
                </Text>
                <Text style={styles.mobileDropdownArrow}>
                  {mobileFilterOpen ? "▲" : "▼"}
                </Text>
              </TouchableOpacity>
            </ScrollView>
            {mobileFilterOpen && (
              <ScrollView
                horizontal
                showsHorizontalScrollIndicator={false}
                contentContainerStyle={styles.mobileChipRow}
              >
                <ItemTrackerBadgeHorizontalList
                  itemsList={operationsFilterCategoryList}
                  filterCategory={selectedCategory || ""}
                  setFilterCategory={setSelectedCategory!}
                  tableHeaders={tableHeaders}
                  trackerType="CUSTOMER"
                  multiSelect
                  selectedCategories={selectedCategories}
                  setSelectedCategories={setSelectedCategories}
                />
              </ScrollView>
            )}
          </View>
        </>
      ) : (
        <>
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            contentContainerStyle={styles.filterContainer}
          >
            <Text style={styles.quickFilterText}>{t("quickFilter")}</Text>
            <ItemTrackerBadgeHorizontalList
              itemsList={operationsFilterCategoryList}
              filterCategory={selectedCategory || ""}
              setFilterCategory={setSelectedCategory!}
              tableHeaders={tableHeaders}
              trackerType="CUSTOMER"
              multiSelect
              selectedCategories={selectedCategories}
              setSelectedCategories={setSelectedCategories}
            />
          </ScrollView>
          <LabelBannerWithSearchBar
            titleName={`${t("totalItems")}: ${filteredOrders.length}`}
            handleSearch={handleSearch}
            setRefresh={setRefresh}
            dataToExport={filteredOrders}
            fileName="ItemTrackerScreen"
            sheetName="Item Tracker Report Screen"
            extraControls={
              <View style={{ flexDirection: "row", gap: 8 }}>
                {!(isSmallDevice || isMobileDevice) && (
                  <View>
                    <TouchableOpacity
                      ref={settingsBtnRef}
                      style={{
                        width: 40,
                        height: 40,
                        borderRadius: 10,
                        backgroundColor: columnSettingsOpen ? "#E0E7FF" : "#F3F4F6",
                        justifyContent: "center",
                        alignItems: "center",
                        borderWidth: 1,
                        borderColor: columnSettingsOpen ? "#818CF8" : "#E2E8F0",
                      }}
                      onPress={() => {
                        if (settingsBtnRef.current) {
                          settingsBtnRef.current.measureInWindow(
                            (x: number, y: number, width: number, height: number) => {
                              setSettingsBtnPos({ x, y, width, height });
                              setColumnSettingsOpen(!columnSettingsOpen);
                            }
                          );
                        } else {
                          setColumnSettingsOpen(!columnSettingsOpen);
                        }
                      }}
                    >
                      <MaterialCommunityIcons
                        name="cog-outline"
                        size={20}
                        color={columnSettingsOpen ? "#56B7FD" : "#6B7280"}
                      />
                    </TouchableOpacity>
                    <Modal
                      visible={columnSettingsOpen}
                      transparent={true}
                      animationType="fade"
                      onRequestClose={() => setColumnSettingsOpen(false)}
                    >
                      <Pressable
                        style={{
                          flex: 1,
                          backgroundColor: "rgba(0,0,0,0.15)",
                        }}
                        onPress={() => setColumnSettingsOpen(false)}
                      >
                        <Pressable
                          style={{
                            position: "absolute",
                            top: settingsBtnPos.y + settingsBtnPos.height + 6,
                            left: settingsBtnPos.x - 180 + 40,
                            backgroundColor: "#fff",
                            borderRadius: 12,
                            borderWidth: 1,
                            borderColor: "#E2E8F0",
                            paddingVertical: 10,
                            minWidth: 220,
                            shadowColor: "#000",
                            shadowOffset: { width: 0, height: 8 },
                            shadowOpacity: 0.15,
                            shadowRadius: 20,
                            elevation: 10,
                          }}
                          onPress={(e) => e.stopPropagation()}
                        >
                          <Text
                            style={{
                              fontSize: 12,
                              fontWeight: "700",
                              color: "#6B7280",
                              paddingHorizontal: 16,
                              paddingBottom: 8,
                              textTransform: "uppercase",
                              letterSpacing: 0.5,
                            }}
                          >
                            Column Visibility
                          </Text>
                          <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 4 }} />
                          {/* Select All */}
                          <TouchableOpacity
                            style={{
                              flexDirection: "row",
                              alignItems: "center",
                              paddingVertical: 8,
                              paddingHorizontal: 16,
                            }}
                            onPress={() => {
                              const allKeys = Object.keys(getAllItemTrackerHeaders()).filter((k) => k !== "");
                              if (hiddenColumns.length === 0) {
                                const updated = [...allKeys];
                                setHiddenColumns(updated);
                                AsyncStorage.setItem("itemtracker_hidden_columns", JSON.stringify(updated));
                              } else {
                                setHiddenColumns([]);
                                AsyncStorage.setItem("itemtracker_hidden_columns", JSON.stringify([]));
                              }
                            }}
                          >
                            <View
                              style={{
                                width: 20,
                                height: 20,
                                borderRadius: 4,
                                borderWidth: 1.5,
                                borderColor: hiddenColumns.length === 0 ? "#56B7FD" : "#D1D5DB",
                                backgroundColor: hiddenColumns.length === 0 ? "#56B7FD" : "#fff",
                                justifyContent: "center",
                                alignItems: "center",
                                marginRight: 10,
                              }}
                            >
                              {hiddenColumns.length === 0 && (
                                <Feather name="check" size={13} color="#fff" />
                              )}
                            </View>
                            <Text
                              style={{
                                fontSize: 14,
                                color: "#111827",
                                fontWeight: "700",
                              }}
                            >
                              Select All
                            </Text>
                          </TouchableOpacity>
                          <View style={{ height: 1, backgroundColor: "#F1F5F9", marginBottom: 2 }} />
                          <ScrollView style={{ maxHeight: 300 }} showsVerticalScrollIndicator={true}>
                            {getOrderedKeys(getAllItemTrackerHeaders()).map((key) => {
                              if (key === "") return null;
                              const value = getAllItemTrackerHeaders()[key];
                              if (!value) return null;
                              if ((value as any).display === "none" || (value as any).display === "hidden") return null;
                              const isVisible = !hiddenColumns.includes(key);
                              const isDragging = dragActiveKey === key;
                              const isDragOver = dragOverKey === key;
                              return (
                                <View
                                  key={key}
                                  {...{ dataSet: { columnkey: key } } as any}
                                  style={{
                                    flexDirection: "row",
                                    alignItems: "center",
                                    paddingVertical: 8,
                                    paddingHorizontal: 16,
                                    opacity: isDragging ? 0.35 : 1,
                                    backgroundColor: isDragOver ? "#EFF6FF" : "transparent",
                                    borderTopWidth: isDragOver ? 2 : 0,
                                    borderTopColor: "#56B7FD",
                                  }}
                                >
                                  <Pressable
                                    onPressIn={() => startColumnDrag(key)}
                                    style={{ padding: 4, marginRight: 6, cursor: "grab" } as any}
                                  >
                                    <Feather name="menu" size={14} color="#9CA3AF" />
                                  </Pressable>
                                  <TouchableOpacity
                                    style={{ flexDirection: "row", alignItems: "center", flex: 1 }}
                                    onPress={() => toggleColumn(key)}
                                  >
                                    <View
                                      style={{
                                        width: 20,
                                        height: 20,
                                        borderRadius: 4,
                                        borderWidth: 1.5,
                                        borderColor: isVisible ? "#56B7FD" : "#D1D5DB",
                                        backgroundColor: isVisible ? "#56B7FD" : "#fff",
                                        justifyContent: "center",
                                        alignItems: "center",
                                        marginRight: 10,
                                      }}
                                    >
                                      {isVisible && (
                                        <Feather name="check" size={13} color="#fff" />
                                      )}
                                    </View>
                                    <Text
                                      style={{
                                        fontSize: 14,
                                        color: isVisible ? "#111827" : "#9CA3AF",
                                        fontWeight: isVisible ? "600" : "400",
                                      }}
                                    >
                                      {(value as any).name}
                                    </Text>
                                  </TouchableOpacity>
                                </View>
                              );
                            })}
                          </ScrollView>
                          {columnOrder.length > 0 && (
                            <>
                              <View style={{ height: 1, backgroundColor: "#F1F5F9", marginTop: 4 }} />
                              <TouchableOpacity
                                style={{
                                  flexDirection: "row",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  paddingVertical: 10,
                                  paddingHorizontal: 16,
                                }}
                                onPress={resetColumnOrder}
                              >
                                <Feather name="rotate-ccw" size={13} color="#6B7280" />
                                <Text style={{ fontSize: 13, color: "#6B7280", fontWeight: "600", marginLeft: 6 }}>
                                  Reset Order
                                </Text>
                              </TouchableOpacity>
                            </>
                          )}
                        </Pressable>
                      </Pressable>
                    </Modal>
                  </View>
                )}
              </View>
            }
          />
        </>
      )}
    </View>
  );

  const onHandleSortColumn = (columnKey: string) => {
    if (sortDirection === "desc") {
      dispatch(sortTrackerOrdersByOrderNumber("asc"));
    } else {
      dispatch(sortTrackerOrdersByOrderNumber("desc"));
    }
  };

  return (
    <ScrollView
      scrollEventThrottle={16}
      onScroll={handleScroll}
      ref={listRef}
      style={styles.container}
    >
      <View style={styles.filterMainContainer}>{renderFilters()}</View>
      {modalOpen && selectedEnquiryType === "add" && (
        <TrackerEnquiryModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "history" && (
        <TrackerHistoryModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      {modalOpen && selectedEnquiryType === "date" && (
        <TrackerDateModal
          onClose={onClose}
          visible={modalOpen}
          order={selectedItem}
        />
      )}

      <View style={styles.contentContainer}>
        {loading ? (
          <View style={styles.spinnerContainer}>
            <Spinner size="small" color="#74BBFF" />
          </View>
        ) : (
          <View style={{ backgroundColor: "#fff" }}>
            <DataTableWithPagination
              isToExpandAllRows={false}
              isRowsExpandable={false}
              headers={nestedTableHeaders}
              itemsPerPage={20}
              isNestedDataLoaded={loading}
              nestedHeader={nestedTableHeaders}
              rows={filteredOrders}
              key="rowKeyValue"
              rowKeyValue="OrderNumber1"
              columnsToNotHaveThousandSeparator={
                columnsToNotHaveThousandsSeparator
              }
              handleSortColumn={onHandleSortColumn}
              operationActions={navigateToOrder}
              itemTracker={true}
              // Required props
              useModal={false}
              setModalVisible={() => { }}
              handleRowClick={() => { }}
              setSelected={() => { }}
              handleDeleteRow={() => { }}
              navigatePressAction={navigatePressAction}
            />
          </View>
        )}
      </View>
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
  },
  headerContainer: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 20,
    paddingHorizontal: 15,
    backgroundColor: "#fff",
    borderBottomLeftRadius: 20,
    borderBottomRightRadius: 20,
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#000",
  },
  newOrderButton: {
    flexDirection: "row",
    alignItems: "center",
    backgroundColor: "#1E88E5",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
  },
  newOrderText: {
    color: "#fff",
    marginRight: 5,
    fontWeight: "bold",
    cursor: "pointer",
  },
  filterMainContainer: {
    width: "100%",
  },
  filterContainer: {
    flexDirection: "row",
    paddingVertical: 10,
    paddingHorizontal: 15,
  },
  operationsContainer: {
    padding: 15,
    backgroundColor: "#fff",
  },
  operationCard: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#fff",
    padding: 15,
    borderRadius: 12,
    marginBottom: 10,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  operationCardContent: {
    flex: 1,
  },
  operationTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 5,
  },
  operationStatus: {
    fontSize: 14,
    color: "#555",
  },
  contentContainer: {
    flex: 1,
    backgroundColor: "#fff",
  },
  spinnerContainer: {
    position: "absolute",
    top: 100,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "rgba(255, 255, 255, 0.8)",
  },
  quickFilterText: {
    display: "flex",
    alignItems: "center",
    marginRight: 10,
  },
  renderFilterStyles: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingHorizontal: 10,
  },
  mobileLayout: {
    flexDirection: "column",
  },
  mobileFilterWrapper: {
    width: "100%",
    paddingTop: 10,
    paddingHorizontal: 10,
    marginBottom: 4,
    gap: 10,
  },
  mobileDropdownButton: {
    flexDirection: "row",
    alignItems: "center",
    paddingVertical: 6,
    paddingHorizontal: 12,
    backgroundColor: "#F1F5F9",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "#E2E8F0",
    alignSelf: "flex-start",
  },
  mobileDropdownButtonActive: {
    borderColor: "#61B8FF",
    backgroundColor: "#EFF6FF",
  },
  mobileDropdownLabel: {
    fontSize: 12,
    fontWeight: "600" as const,
    color: "#6B7280",
  },
  mobileDropdownValue: {
    fontSize: 12,
    fontWeight: "700" as const,
    color: "#1E40AF",
  },
  mobileDropdownArrow: {
    fontSize: 8,
    color: "#6B7280",
    marginLeft: 4,
  },
  mobileChipRow: {
    flexDirection: "row" as const,
    paddingVertical: 8,
    paddingHorizontal: 4,
  },
  fab: {
    position: "absolute" as const,
    margin: 16,
    right: 0,
    zIndex: 1,
  },
});
